/**
 * Parses eBay API error responses and formats them for display
 * @param {Object} response - The eBay API response object
 * @returns {Object} - Formatted error information
 */
export const parseEbayErrors = (response) => {
  // Check if responses are nested under details
  const responses = response.responses || (response.details && response.details.responses);
  
  // If response has a responses array (bulk operations)
  if (responses && Array.isArray(responses)) {
    const allErrors = [];
    
    responses.forEach((item, index) => {
      const sku = item.sku || `Item ${index + 1}`;
      
      // Process errors
      if (item.errors && Array.isArray(item.errors)) {
        item.errors.forEach(error => {
          allErrors.push({
            sku,
            message: error.message,
            parameters: error.parameters || []
          });
        });
      }
    });
    
    // If no errors found in responses, check for message in main object
    if (allErrors.length === 0) {
      if (response.message) {
        return {
          hasErrors: true,
          errors: [{
            sku: 'General',
            message: response.message,
            parameters: []
          }],
          formattedMessage: response.message
        };
      }
      if (response.details && response.details.message) {
        return {
          hasErrors: true,
          errors: [{
            sku: 'General',
            message: response.details.message,
            parameters: []
          }],
          formattedMessage: response.details.message
        };
      }
    }
    
    return {
      hasErrors: allErrors.length > 0,
      errors: allErrors,
      formattedMessage: formatErrorMessages(allErrors)
    };
  }
  
  // If response has direct errors array
  if (response.errors && Array.isArray(response.errors)) {
    const errors = response.errors.map(error => ({
      sku: 'Unknown',
      message: error.message,
      parameters: error.parameters || []
    }));
    
    return {
      hasErrors: true,
      errors,
      formattedMessage: formatErrorMessages(errors)
    };
  }
  
  // If response has a single error message
  if (response.message) {
    return {
      hasErrors: true,
      errors: [{
        sku: 'Unknown',
        message: response.message,
        parameters: []
      }],
      formattedMessage: response.message
    };
  }
  
  // Default fallback
  return {
    hasErrors: false,
    errors: [],
    formattedMessage: 'An unknown error occurred'
  };
};

/**
 * Formats error messages for display
 * @param {Array} errors - Array of error objects
 * @returns {string} - Formatted message string
 */
const formatErrorMessages = (errors) => {
  let message = '';
  
  if (errors.length > 0) {
    message += 'Errors:\n';
    errors.forEach((error, index) => {
      message += `${index + 1}. SKU: ${error.sku}\n`;
      message += `   ${error.message}\n\n`;
    });
  }
  
  return message.trim();
};

/**
 * Creates a detailed error notification message
 * @param {Object} response - The eBay API response object
 * @returns {string} - Detailed error message for notification
 */
export const createDetailedErrorMessage = (response) => {
  const parsed = parseEbayErrors(response);
  
  if (!parsed.hasErrors) {
    return 'An unknown error occurred during the eBay sync operation.';
  }
  
  let message = '';
  
  if (parsed.hasErrors) {
    const errorCount = parsed.errors.length;
    message += `${errorCount} error${errorCount > 1 ? 's' : ''} occurred:\n`;
    
    parsed.errors.forEach((error, index) => {
      message += `• ${error.sku}: ${error.message}`;
      if (index < parsed.errors.length - 1) message += '\n';
    });
  }
  
  return message;
}; 